import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { RefreshCw, Pencil, X, CalendarClock } from 'lucide-react';
import { api } from '../lib/api';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';

export const RecheckBanner = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [info, setInfo] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!user) { setInfo(null); return; }
    api.get('/recheck').then(({ data }) => setInfo(data)).catch(() => {});
  }, [user]);

  if (!info?.due) return null;
  if (info.dismissed_until && new Date(info.dismissed_until) > new Date()) return null;

  const rerun = async () => {
    setBusy(true);
    try {
      const { data } = await api.post('/recheck/rerun');
      sessionStorage.setItem('match_results', JSON.stringify(data.results));
      toast.success(`Re-checked ${data.count} programs with your saved answers`);
      navigate('/results');
    } catch { toast.error('Could not re-run your check'); }
    finally { setBusy(false); }
  };

  const dismiss = async () => { await api.post('/recheck/dismiss'); setInfo(null); };
  const months = Math.floor(info.days_since / 30);

  return (
    <section className="max-w-7xl mx-auto px-4 sm:px-6 pt-6" data-testid="recheck-banner" role="status">
      <div className="rounded-2xl border-2 border-primary/30 bg-primary/5 p-5 flex flex-col md:flex-row md:items-center gap-4">
        <div className="w-11 h-11 rounded-xl bg-primary text-primary-foreground flex items-center justify-center shrink-0"><CalendarClock className="w-5 h-5" /></div>
        <div className="flex-1">
          <div className="font-semibold" style={{fontFamily: 'Lexend'}}>It's been {months} month{months === 1 ? '' : 's'} since your last benefits check</div>
          <p className="text-sm text-muted-foreground mt-0.5">Income, housing, DTC approval or a new diagnosis can unlock different programs. Has anything changed?</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button onClick={rerun} disabled={busy} className="bg-primary hover:bg-primary/90 text-primary-foreground" data-testid="recheck-rerun-btn"><RefreshCw className={`w-4 h-4 mr-2 ${busy ? 'animate-spin' : ''}`} /> Nothing changed — re-run</Button>
          <Button variant="outline" onClick={() => navigate('/find-benefits')} data-testid="recheck-update-btn"><Pencil className="w-4 h-4 mr-2" /> Update my answers</Button>
          <Button variant="ghost" size="icon" onClick={dismiss} aria-label="Remind me later" data-testid="recheck-dismiss-btn"><X className="w-4 h-4" /></Button>
        </div>
      </div>
    </section>
  );
};
